/** Manages the logic for toggling a task's completion status. */
// @ts-check
import { z } from 'zod';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import { useAuth } from '@/features/auth/useAuth';
import { updateTaskSchema } from '@/features/tasks/utils/data/taskSchema';
import { executeToggleComplete } from '@/features/tasks/utils/mutations/taskToggleUtils';

/**
 * @param {Task} task
 * @param {boolean} completed
 * @param {TasksContextValue} tasksContext
 * @param {Object} focusData
 * @param {string} userId
 */
const performToggleAction = async (
  task,
  completed,
  tasksContext,
  focusData,
  userId,
) => {
  try {
    const validatedData = updateTaskSchema.parse({ completed });
    const {
      activeFocusTaskId = null,
      focusStartTime = null,
      stopFocus = () => {},
    } = focusData;
    await executeToggleComplete(
      task,
      validatedData,
      tasksContext,
      { activeFocusTaskId, focusStartTime, stopFocus },
      userId,
    );
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error(
        'Zod Validation Error:',
        JSON.stringify(error.flatten(), null, 2),
      );
    } else {
      console.error('General Error in useToggleTaskComplete:', error);
    }
  }
};

const useToggleTaskComplete = () => {
  const tasksContext = useTasksContext();
  const { user } = useAuth();

  /**
   * @param {Task} task
   * @param {boolean} completed
   * @param {Object} [focusData]
   */
  const handleToggleComplete = async (task, completed, focusData = {}) => {
    if (!user) {
      console.error('User not available for toggle complete action.');
      return;
    }
    await performToggleAction(
      task,
      completed,
      tasksContext,
      focusData,
      user.uid,
    );
  };

  return { handleToggleComplete };
};

export default useToggleTaskComplete;
